import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { InvoiceInterface, InvoiceInterfaceWithId } from '@sk/sk-interfaces';
import { forkJoin, Observable } from 'rxjs';
import { concatAll, map, take, tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';
import { LocalStorageService } from 'services/local-storage.service';
import { fromPromise } from 'rxjs/internal-compatibility';
import firebase from 'firebase/app';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {

  constructor(private http: HttpClient,
              private storageService: LocalStorageService,
              private firestoreService: AngularFirestore) {
  }

  getInvoices(status: string = 'all'): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices', ref => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;

        query = query.orderBy('dueDate', 'desc');
        if (status !== 'all') {
          query = query.where('status', '==', status);
        }
        return query;
      }).snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          const invNew: InvoiceInterfaceWithId = { ...data, id, exists };
          return invNew;
        }))
      );
  }


  getLimitedInvoices(): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices', ref => ref.orderBy('dueDate', 'desc').limit(250)).snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          const invNew: InvoiceInterfaceWithId = { ...data, id, exists };
          return invNew;
        }))
      );
  }


  getCreatedInvoices(): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices', ref => {
        return ref
          .where('status', '==', 'created');

      }).snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { ...data, id, exists } as InvoiceInterfaceWithId;
        }))
      );
  }

  getInvoice(id: string) {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices').doc(id).get()
      .pipe(
        map(res => {
          const invoice = res.data() as InvoiceInterface;
          const exists = res.exists;
          return { ...invoice, id, exists } as InvoiceInterfaceWithId;
        }));
  }


  getInvoiceById(id: string) {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices').doc(id).snapshotChanges()
      .pipe(
        map(res => {
          const invoice = res.payload.data() as InvoiceInterface;
          const exists = res.payload.exists;
          return { ...invoice, id, exists } as InvoiceInterfaceWithId;
        }));
  }


  getInvoiceWithOrganizationId(id: string, organizationId: string) {
    return this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices').doc(id).get()
      .pipe(
        map(res => {
          if (res.exists) {
            const invoice = res.data() as InvoiceInterfaceWithId;
            invoice.exists = res.exists;
            invoice.id = id;
            return invoice;
          } else {
            return null;
          }
        }));
  }

  addInvoice(invoice: InvoiceInterface) {
    const organizationId = this.storageService.getStorage('organizationId');
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId)
      .collection('invoices').add(invoice)).pipe(
      map(res => {
        const id = res.id;
        const exists = true;
        const newInvoice = { ...invoice, id, exists } as InvoiceInterfaceWithId;
        return newInvoice;
      }));
  }

  addInvoices(invoices: InvoiceInterface[]): Observable<InvoiceInterfaceWithId> {
    const organizationId = this.storageService.getStorage('organizationId');

    const observables: Observable<InvoiceInterfaceWithId>[] = [];
    invoices.forEach(invoice => {
      observables.push(
        fromPromise(this.firestoreService
          .collection('organizations')
          .doc(organizationId)
          .collection('invoices').add(invoice))
          .pipe(
            map(res => {
              const id = res.id;
              const exists = true;
              return { ...invoice, id, exists } as InvoiceInterfaceWithId;
            })));
    });

    return forkJoin(observables)
      .pipe(concatAll());
  }


  addInvoices2(invoices: InvoiceInterface[]): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getStorage('organizationId');
    const observables: Observable<InvoiceInterfaceWithId>[] = [];
    invoices.forEach(invoice => {
      observables.push(
        fromPromise(this.firestoreService
          .collection('organizations')
          .doc(organizationId)
          .collection('invoices').add(invoice))
          .pipe(
            map(res => {
              const id = res.id;
              const exists = true;
              return { ...invoice, id, exists } as InvoiceInterfaceWithId;
            })));
    });

    return forkJoin(observables);
  }


  updateInvoice(invoice, invoiceId: string) {
    const organizationId = this.storageService.getStorage('organizationId');
    delete invoice.id;
    delete invoice.exists;
    if (!invoiceId) {
      return fromPromise(Promise.reject(new Error('Invoice Id undefined')));
    }
    return fromPromise(this.firestoreService
      .collection('organizations').doc(organizationId)
      .collection('invoices').doc(invoiceId).update(invoice));
  }




  exactSyncInvoices() {
    const organizationId = this.storageService.getStorage('organizationId');
    return this.http
      .post<string>(environment.api_url + '/syncExactInvoices2', JSON.stringify({ organizationId }))
      .pipe(
        take(1)
      );
  }

  /**
   *
   * @param invoiceId
   */
  getExactInvoiceStatus(invoiceId: string) {
    return this.http
      .post<string>(environment.api_url + '/requestExactInvoiceStatus', JSON.stringify({ invoiceId }))
      .pipe(
        take(1),
        map((res: any) => {
          console.log(res);
          if (res.type === 'success') {
            return res;
          } else {
            if (!environment.production) {
              console.log('Error getting invoice status', res);
            }
            return res;
          }
        })
      );
  }



  // getFilteredInvoices(locationId: string, orderId: string,
  //                     requestId: string, projectId: string, clientId: string,
  //                     filterValue: string, limit: number): Observable<InvoiceInterfaceWithId[]> {
  //   const organizationId = this.storageService.getOrganizationId();
  //
  //   console.log('LocId: ', locationId);
  //   console.log('orderId: ', orderId);
  //   console.log('projectId: ', projectId);
  //   console.log('requestId: ' + requestId);
  //   console.log('clientId: ' + clientId);
  //   console.log('limit: ', limit);
  //   console.log('filter: ', filterValue);
  //   let applyFilter = null;
  //   if (filterValue !== 'all') {
  //     applyFilter = filterValue;
  //   }
  //
  //   const invoiceRef = this.firestoreService.collection('organizations').doc(organizationId).collection('invoices', ref => {
  //     let query: firebase.firestore.CollectionReference | firebase.firestore.Query =
  //       ref;
  //     if (locationId) {
  //       query = query.where('locationId', '==', locationId);
  //     }
  //     if (orderId) {
  //       query = query.where('orderId', '==', orderId);
  //     }
  //     if (clientId) {
  //       query = query.where('clientId', '==', clientId);
  //     }
  //     if (projectId) {
  //       query = query.where('projectId', '==', projectId);
  //     }
  //     if (requestId) {
  //       query = query.where('requestId', '==', requestId);
  //     }
  //     if (applyFilter) {
  //       query = query.where('status', '==', applyFilter);
  //     }
  //     if (limit) {
  //       query = query.limit(limit);
  //     }
  //     return query.orderBy('invoiceDate', 'desc');
  //   });
  //
  //   return invoiceRef.snapshotChanges()
  //     .pipe(
  //       map(actions => actions.map(a => {
  //         const data = a.payload.doc.data() as InvoiceInterface;
  //         const id = a.payload.doc.id;
  //         const exists = a.payload.doc.exists;
  //         return { id, exists, ...data } as InvoiceInterfaceWithId;
  //       }))
  //     );
  // }


  getInvoicesPerLocation(locationId: string, filterValue?: string, limit?: number): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getOrganizationId();

    if (!locationId) {
      return;
    }


    console.log('LocId: ', locationId);
    console.log('limit: ', limit);

    let applyFilter = null;
    if (filterValue && filterValue !== 'all') {
      applyFilter = filterValue;
    }

    const invoiceRef = this.firestoreService.collection('organizations').doc(organizationId).collection('invoices', ref => {
      let query: firebase.firestore.CollectionReference | firebase.firestore.Query = ref;
      query = query.orderBy('invoiceDate', 'desc')
        .where('locationId', '==', locationId);
      if (applyFilter) {
        query = query.where('status', '==', applyFilter);
      }
      if (limit) {
        query = query.limit(limit);
      }
      return query;
    });

    return invoiceRef.snapshotChanges()
      .pipe(
        tap(kist => console.table(kist)),
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { id, exists, ...data } as InvoiceInterfaceWithId;
        }))
      );
  }

  /**
   *
   * @param projectId
   * @param limit
   */
  getInvoicesPerProject(projectId: string, filterValue?: string, limit?: number): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getOrganizationId();

    if (!projectId) {
      return;
    }

    let applyFilter = null;
    if (filterValue && filterValue !== 'all') {
      applyFilter = filterValue;
    }

    const invoiceRef = this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices', ref => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query =
          ref.where('projectId', '==', projectId)
            .orderBy('invoiceDate', 'desc');
        if (applyFilter) {
          query = query.where('status', '==', applyFilter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query;
      });

    return invoiceRef.snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { id, exists, ...data } as InvoiceInterfaceWithId;
        }))
      );
  }


  /**
   *
   * @param orderId
   * @param limit
   */
  getInvoicesPerOrder(orderId: string, filterValue?: string, limit?: number): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getOrganizationId();

    if (!orderId) {
      return;
    }

    console.log('orderId: ', orderId);
    let applyFilter = null;
    if (filterValue !== 'all') {
      applyFilter = filterValue;
    }

    const invoiceRef = this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices', ref => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query =
          ref.where('orderId', '==', orderId);
        if (applyFilter) {
          query = query.where('status', '==', applyFilter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query.orderBy('invoiceDate', 'desc');
      });

    return invoiceRef.snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { id, exists, ...data } as InvoiceInterfaceWithId;
        }))
      );
  }


  /**
   *
   * @param orderId
   * @param limit
   */
  getInvoicesPerRequest(requestId: string, filterValue?: string, limit?: number): Observable<InvoiceInterfaceWithId[]> {
    const organizationId = this.storageService.getOrganizationId();

    console.log('requestId: ', requestId);
    console.log('limit: ', limit);
    let applyFilter = null;
    if (filterValue !== 'all') {
      applyFilter = filterValue;
    }

    const invoiceRef = this.firestoreService.collection('organizations').doc(organizationId)
      .collection('invoices', ref => {
        let query: firebase.firestore.CollectionReference | firebase.firestore.Query =
          ref.where('requestId', '==', requestId);
        if (applyFilter) {
          query = query.where('status', '==', applyFilter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        return query.orderBy('invoiceDate', 'desc');
      });

    return invoiceRef.snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as InvoiceInterface;
          const id = a.payload.doc.id;
          const exists = a.payload.doc.exists;
          return { id, exists, ...data } as InvoiceInterfaceWithId;
        }))
      );
  }


  emailPDFInvoices(invoiceIds: string[]) {
    const payload = {
      invoiceIds: invoiceIds
    };
    return this.http
      .post<string>(environment.api_url + '/emailPDFInvoicesPerOrg', payload)
      .pipe(
        take(1)
      );
  }


  emailPDFInvoice(invoiceId: string) {
    const payload = {
      invoiceId: invoiceId
    };
    return this.http
      .post<string>(environment.api_url + '/resendEmailPdfInvoice', payload)
      .pipe(
        take(1)
      );
  }
}
